import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Alert,
  Chip,
  Button,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { adminApi, Organization } from '../services/api';
import OrganizationDialog from '../components/OrganizationDialog';

const OrganizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await adminApi.listOrganizations();
      
      if (response.success) {
        setOrganizations(response.data.organizations);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleRowClick = (params: GridRowParams) => {
    navigate(`/organizations/${params.row.organizationId || params.row.id || params.row.name}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Organization ID',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {params.value || params.row.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Display Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      valueGetter: (value: string | null) => value || 'active',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.value}
            size="small"
            color={params.value === 'inactive' ? 'default' : params.value === 'trial' ? 'warning' : 'success'}
          />
        </Box>
      ),
    },
    {
      field: 'domains',
      headerName: 'Domains',
      flex: 1,
      minWidth: 180,
      valueGetter: (value: string[] | null) => (value && value.length ? value.join(', ') : '-'),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">{params.value}</Typography>
        </Box>
      ),
    },
  ];

  const handleSuccess = async () => {
    await fetchOrganizations();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" fontWeight={700}>
            Organizations
          </Typography>
          <Chip label={`${organizations.length} organizations`} color="primary" variant="outlined" />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Create Organization
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={organizations}
          columns={columns}
          getRowId={(row) => row.id || row.name}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          onRowClick={handleRowClick}
          autoHeight
          sx={{
            border: 'none',
            cursor: 'pointer',
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
          }}
        />
      </Card>

      <OrganizationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={handleSuccess}
      />
    </Box>
  );
};

export default OrganizationsPage;
